import type {
    AbstractControl,
    FormArray,
    FormControl,
    FormGroup,
    FormRecord,
    UntypedFormControl,
} from '@angular/forms';
import type { Observable, ObservedValueOf } from 'rxjs';
import { takeUntil } from 'rxjs';
import type { SortableEvent, SortableOptions } from 'sortablejs';
import type { SelectedFileExtended } from '@common/types/file.type';
import type { ReplacedFile } from '@components/sp-file-manager/src/types';
import { SpFileManagerComponentModule } from '@components/sp-file-manager';
import type { SelectedFileInterface } from '@sendpulse/file-manager';
import type { GalleryOptions, GalleryTypes, GalleryWidgetData } from '@web/widgets/gallery/type';

import { produce } from 'immer';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { DirectoryItemCategory, FmSourceType } from '@sendpulse/file-manager';
import { SortablejsModule } from '@moddi3/ngx-sortablejs';
import { buildFileSrcSets } from '@components/sp-file-manager/src/helpers';
import { PreviewType } from '@components/sp-file-manager/src/types';

import { EGalleryTypes, type EHorizontalPosition, WidgetsDataTypes } from '@common/enums';
import { extendWebFileDataPromise } from '@common/helpers';

import { WebBaseControlComponent } from '@web/widgets/common/abstract/web-base-control.component';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

import { defaultOptions } from '@web/widgets/gallery/model/defaultOptions';
import { ImageSettingsComponent } from './image-settings/image-settings.component';
import { GallerySettingsComponent } from './gallery-settings/gallery-settings.component';
import { WebWidgetHideSettingsComponent } from '@web/shared/components/web-widget-hide-settings';
import { ERadioGroupType } from '@ui/sp-radio-group';
import { environment } from '@web-env/environment';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { UseUnsplash } from '@web/shared/hooks/use-unsplash.hook';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ImageSizeLimitModalComponent } from '@web/shared/modals/image-size-limit-modal/image-size-limit-modal.component';

interface GalleryForm {
    type: GalleryTypes;
    options: ObservedValueOf<GallerySettingsComponent['formReady']>;
    images: SelectedFileExtended[];
    horizontalAlign: EHorizontalPosition;
    maxWidth: number;
    resizedHeight: number;
    hideOnMobile: boolean;
    hideOnTablet: boolean;
    hideOnDesktop: boolean;
    isSynchronizeStyle: boolean;
    position: {
        x: number;
        y: number;
        rotate: number;
        zIndex: number;
        mobileX: number;
        mobileY: number;
        mobileRotate: number;
        mobileZIndex: number;
        width: number;
    };
}

@Component({
    selector: 'web-gallery-control',
    templateUrl: './gallery-control.component.html',
    styleUrls: ['./gallery-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonElementsModule,
        GallerySettingsComponent,
        ImageSettingsComponent,
        SpFileManagerComponentModule,
        SortablejsModule,
        TooltipModule,
        WebWidgetHideSettingsComponent,
    ],
})
export class GalleryControlComponent extends WebBaseControlComponent<GalleryWidgetData> {
    private readonly siteFacade: SiteFacade = inject(SiteFacade);
    private readonly useUnsplash: UseUnsplash = inject(UseUnsplash);
    private readonly modalService: BsModalService = inject(BsModalService);

    public readonly galleryTypesOptions = [
        { label: 'gallery_carousel', type: EGalleryTypes.carousel },
        { label: 'gallery_grid', type: EGalleryTypes.grid },
        { label: 'gallery_collage', type: EGalleryTypes.collage },
    ];

    public readonly PreviewType = PreviewType;
    public readonly FmSourceType = FmSourceType;
    public readonly DirectoryItemCategory = DirectoryItemCategory;
    public editableGallery = null;
    public editableImage = null;
    public editableImageIndex = null;
    public columnWidth: number = 0;
    public buttonDisabled: boolean = false;

    // дефолт true, щоб перший init на десктопі відпрацював коректно ще до першого emit widget$
    private isDeviceDesktop: boolean = true;
    // дефолт maxWidth, якщо віджет додають у планшет/мобайл-режимі (де реальну ширину колонки не виміряти)
    private readonly DEFAULT_MAX_WIDTH: number = 1136;

    public sortableImages: SelectedFileExtended[] = [];
    public sortableOptions: SortableOptions = {
        onUpdate: () => this.drop(),
    };

    public readonly userId$: Observable<number> = this.userFacade.userId$;

    // викликається в ngOnInit ДО підписки на widget$, а store.select емітить синхронно —
    // тож isDeviceDesktop буде актуальним вже на першому setFormData()
    protected beforeFormSubscription(): void {
        this.isDeviceDesktop$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((isDesktop: boolean) => (this.isDeviceDesktop = isDesktop));
    }

    protected initForm(): void {
        this.controlForm = this.fb.group({
            data: this.fb.group<GalleryForm>({
                type: null,
                options: null,
                images: null,
                hideOnMobile: null,
                hideOnTablet: null,
                hideOnDesktop: null,
                isSynchronizeStyle: null,
                horizontalAlign: null,
                maxWidth: null,
                resizedHeight: null,
                position: this.fb.group({
                    x: null,
                    y: null,
                    rotate: null,
                    zIndex: null,
                    mobileX: null,
                    mobileY: null,
                    mobileRotate: null,
                    mobileZIndex: null,
                    width: null,
                }) as any,
            }),
        });
    }

    protected setFormData(): void {
        this.controlForm.patchValue({ data: this.widgetData }, { emitEvent: false });
        this.sortableImages = [...(this.widgetData.images || [])];

        // якщо панель налаштувань відкрита — оновлюємо її item з актуальних опцій стора,
        // щоб зміни з в'ю (напр. ресайз висоти thumbs) відображались в інпутах панелі
        if (this.editableGallery) {
            this.editableGallery = this.widgetData.options;
        }

        // TODO: TMP KOSTIL REFACTOR
        this.columnWidth = 0;
        if (!this.isSpecific) {
            this.columnWidth = (
                document.querySelector(`[id='${this.widget.columnId}'] .gallery-widget-width`) as HTMLDivElement
            )?.offsetWidth;
        } else {
            this.columnWidth = document.getElementById(this.widget.columnId)?.offsetWidth;
        }

        // Підрівнюємо maxWidth під ширину колонки лише в десктоп-режимі: у планшет/мобайл-симуляції
        // контейнер (.block) навмисно звужений (840/480), тож його ширина не має чіпати десктопний maxWidth.
        if (this.isDeviceDesktop) {
            if (!this.maxWidth.value) {
                this.maxWidth.setValue(this.columnWidth, { emitEvent: false });
            }

            if (this.columnWidth && this.columnWidth < this.maxWidth.value) {
                this.maxWidth.setValue(this.columnWidth, { emitEvent: false });
            }
        } else if (!this.maxWidth.value) {
            // віджет додано в планшет/мобайл-режимі — реальну ширину колонки не виміряти, ставимо безпечний дефолт
            this.maxWidth.setValue(this.DEFAULT_MAX_WIDTH, { emitEvent: false });
        }

        if (this.isSynchronizeStyle.value === null || this.isSynchronizeStyle.value === undefined) {
            this.isSynchronizeStyle.setValue(true, { emitEvent: false });
        }
    }

    public addChildForm<K extends keyof GalleryForm>(name: K, group: Exclude<GalleryForm[K], undefined>) {
        this.controlForm.setControl(name, group, { emitEvent: false });
    }

    public patchOptions(options: GalleryOptions, emitEvent = false) {
        this.data.patchValue({ options }, { emitEvent });
    }

    public saveAndClose(options: GalleryOptions) {
        const emitEvent = true;
        this.patchOptions(options, emitEvent);
        this.editGallery(null);
    }

    public setGalleryType(event: Event): void {
        const target = event.target as HTMLSelectElement;
        const type = target.value as GalleryTypes;
        const currType = this.galleryType.value;
        if (type === currType) return;

        const options = { ...defaultOptions[type] };
        const currOptions = { ...this.options.value };
        Object.keys(options).forEach((key) => {
            if (key in currOptions) {
                options[key] = currOptions[key];
            }
        });

        this.data.patchValue({ options, type });
    }

    public editGallery(gallery): void {
        this.editableGallery = gallery;
    }

    public editImage(image, index: number = null): void {
        this.editableImage = image;
        this.editableImageIndex = index;
    }

    public setImageData(imageData: SelectedFileExtended): void {
        const images = produce(this.images.value as any[], (draft) => {
            draft[this.editableImageIndex] = { ...this.images.value[this.editableImageIndex], ...imageData };
        });

        this.images.setValue(images);
    }

    public removeImage(index: number): void {
        const newImages = produce(this.images.value as any[], (draft) => {
            draft.splice(index, 1);
        });

        this.images.setValue(newImages);
    }

    public async replaceImage({ index, file }: ReplacedFile): Promise<void> {
        const extendedFile = await extendWebFileDataPromise(file, environment.fileManagerCdnUrl);

        if (extendedFile.imageHeight >= 8000 || extendedFile.imageWidth >= 8000) {
            this.modalService.show(ImageSizeLimitModalComponent, { initialState: { imageName: extendedFile.name } });
        } else {
            const newImages = produce(this.images.value as SelectedFileExtended[], (draft) => {
                draft.splice(index, 1, extendedFile);
            });

            this.images.setValue(newImages);

            this.siteFacade.resizeImage({
                path: extendedFile.path,
                url: `${environment.fileManagerCdnUrl}${extendedFile.url}`,
            });
        }
    }

    public async setImages(files: SelectedFileInterface[]): Promise<void> {
        this.buttonDisabled = true;

        if (!files) return;

        for (const file of files) {
            const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(
                file,
                environment.fileManagerCdnUrl
            );

            if (extendedFile.imageHeight >= 8000 || extendedFile.imageWidth >= 8000) {
                this.modalService.show(ImageSizeLimitModalComponent, {
                    initialState: { imageName: extendedFile.name },
                });
            } else {
                const srcSets = buildFileSrcSets(extendedFile, environment.fileManagerCdnUrl);

                this.images.setValue([...this.images.value, { ...extendedFile, ...{ srcSets } }]);

                this.siteFacade.resizeImage({
                    path: extendedFile.path,
                    url: `${environment.fileManagerCdnUrl}${extendedFile.url}`,
                });
            }
        }
        this.buttonDisabled = false;

        this.changeDetectorRef.detectChanges();
    }

    public drop(): void {
        this.images.setValue(this.sortableImages);
    }

    public showEditImageModal(index: number) {
        this.showImageEditor(this.images.value[index], WidgetsDataTypes.images, index);
    }

    public imageOwnerUserId(index: number): number {
        const urlParts = this.images.value[index].url.split('/');
        return Number(urlParts[1].substring(32));
    }

    handleImageReplace(eventType: 'fm' | 'stock', i: number, fmReplaceFunction: (i: number) => void): void {
        if (eventType === 'fm') {
            fmReplaceFunction(i);
        } else if (eventType === 'stock') {
            this.openUnsplashModal({ replaceIndex: i });
        }
    }

    public openUnsplashModal({ replaceIndex }: { replaceIndex: number | null } = { replaceIndex: null }): void {
        this.useUnsplash.openUnsplashModal({
            index: replaceIndex || replaceIndex === 0 ? replaceIndex : this.images.value.length,
            widgetId: this.widget.id,
            dataType: WidgetsDataTypes.images,
        });
    }

    public setHorizontalAlign(value: string): void {
        this.horizontalAlign.setValue(value);
    }

    public refreshMaxWidth(): void {
        this.maxWidth.setValue(this.columnWidth);
    }

    public setMaxWidth(value: number): void {
        this.maxWidth.setValue(value);
    }

    public get options(): FormRecord {
        return this.data.get('options') as FormRecord;
    }

    public get galleryType(): AbstractControl {
        return this.data.get('type');
    }

    public get images(): FormArray {
        return this.data.get('images') as FormArray;
    }

    public get position(): any {
        return this.data.get('position') as FormGroup<any>;
    }

    public get hideOnMobile(): FormControl<boolean> {
        return this.data.get('hideOnMobile') as FormControl<boolean>;
    }

    public get hideOnTablet(): FormControl<boolean> {
        return this.data.get('hideOnTablet') as FormControl<boolean>;
    }

    public get hideOnDesktop(): FormControl<boolean> {
        return this.data.get('hideOnDesktop') as FormControl<boolean>;
    }

    public setHideOnMobile(value: boolean): void {
        this.hideOnMobile.setValue(value);
    }

    public setHideOnTablet(value: boolean): void {
        this.hideOnTablet.setValue(value);
    }

    public setHideOnDesktop(value: boolean): void {
        this.hideOnDesktop.setValue(value);
    }

    public get horizontalAlign(): UntypedFormControl {
        return this.data.get('horizontalAlign') as UntypedFormControl;
    }

    public get maxWidth(): UntypedFormControl {
        return this.data.get('maxWidth') as UntypedFormControl;
    }

    public get resizedHeight(): UntypedFormControl {
        return this.data.get('resizedHeight') as UntypedFormControl;
    }

    public get isSynchronizeStyle(): FormControl<boolean> {
        return this.data.get('isSynchronizeStyle') as FormControl<boolean>;
    }

    public setSynchronizeStyle(): void {
        this.isSynchronizeStyle.setValue(!this.isSynchronizeStyle.value);
    }

    protected readonly ERadioGroupType = ERadioGroupType;
}
